// src/pages/RestaurantOwner/Menu.jsx - FIXED: Stock toggle always clickable

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  ChefHat,
  Tag,
  Star,
  Loader,
  Flame,
  Check,
  PackageX,
  PackageCheck,
  ImageIcon
} from 'lucide-react';
import StorageService from '../../services/StorageService';
import ImageOptimizationService from '../../services/ImageOptimizationService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebaseConfig';
import { doc, deleteDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableImage = React.memo(({ id, src, onRemove, uniqueKey }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: uniqueKey });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '80px',
    height: '80px',
    position: 'relative',
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab'
  };
  
  const handleRemove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove(id);
  }, [id, onRemove]);
  
  return (
    <div ref={setNodeRef} style={style} className="border rounded overflow-hidden">
      <div {...attributes} {...listeners} className="w-full h-full relative">
        <img src={src} alt="menu" className="w-full h-full object-cover pointer-events-none" draggable={false} />
      </div>
      <button
        type="button"
        onClick={handleRemove}
        className="absolute -top-1 -right-1 bg-red-500 text-white w-6 h-6 flex items-center justify-center rounded-full text-sm hover:bg-red-600 cursor-pointer z-20 shadow-lg border-2 border-white"
        style={{ pointerEvents: 'auto' }}
      >
        ×
      </button>
    </div>
  );
});

SortableImage.displayName = 'SortableImage';

const OwnerMenu = ({ restaurantId, onNavigateToCategories }) => {
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState(['Veg', 'Non-Veg', 'Vegan']);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [optimizationInfo, setOptimizationInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    priceHalf: '',
    priceFull: '',
    category: '',
    subcategory: '',
    description: '',
    rating: 4,
    ratingsCount: 1,
    portionOptions: ['Half', 'Full'],
    images: [],
    newImages: [],
    inStock: true,
    recipe: { ingredients: [] } // ✅ ADD THIS LINE
  });
  
  const [imageKeys, setImageKeys] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]); // ✅ ADD THIS LINE

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    loadMenu();
  }, [restaurantId]);

  const loadMenu = async () => {
    try {
      setLoading(true);
      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      if (restaurant) {
        const menuWithStock = (restaurant.menu || []).map(item => ({
          ...item,
          inStock: item.inStock !== undefined ? item.inStock : true
        }));
        
        setMenu(menuWithStock);
        setCategories(restaurant.categories || []);
        setSubcategories(restaurant.subcategories || ['Veg', 'Non-Veg', 'Vegan']);
        setFeaturedItems(restaurant.featuredItems || []);
         // ✅ ADD THESE 3 LINES:
      const inventorySnapshot = await getDocs(collection(db, 'restaurants', restaurantId, 'inventory_items'));
      const items = inventorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInventoryItems(items);

      } else {
        setMenu([]);
        setCategories([]);
        setSubcategories(['Veg', 'Non-Veg', 'Vegan']);
        setFeaturedItems([]);
      }
    } catch (error) {
      console.error('❌ Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setUploadProgress(0);
    setOptimizationInfo(null);
    setFormData({
      name: '',
      priceHalf: '',
      priceFull: '',
      category: '',
      subcategory: '',
      description: '',
      rating: 4,
      ratingsCount: 1,
      portionOptions: ['Half', 'Full'],
      images: [],
      newImages: [],
      inStock: true,
      recipe: { ingredients: [] } // ✅ ADD THIS LINE
    });
    setImageKeys([]);
  };

  const uploadImagesToStorage = async (files) => {
  const uploadedUrls = [];
  
  try {
    console.log('🔄 Starting image optimization...');
    
    // Optimize all images
    const optimizedBlobs = await ImageOptimizationService.optimizeMultiple(
      files,
      (progress) => {
        console.log(`📊 Optimization progress: ${progress.percentage}%`);
        setUploadProgress(Math.round(progress.percentage * 0.3)); // 30% for optimization
      }
    );
    
    // Show savings info
    const savings = ImageOptimizationService.calculateSavings(files, optimizedBlobs);
    setOptimizationInfo(savings);
    console.log('💰 Storage savings:', savings);
    
    // Upload optimized images
    for (let i = 0; i < optimizedBlobs.length; i++) {
      const blob = optimizedBlobs[i];
      const originalFile = files[i];
      const timestamp = Date.now();
      const fileName = `menu/${restaurantId}/${timestamp}_${i}_${originalFile.name}`;
      const storageRef = ref(storage, fileName);
      
      try {
        console.log(`📤 Uploading optimized image ${i + 1}/${optimizedBlobs.length}...`);
        setUploadProgress(Math.round(30 + ((i + 0.5) / optimizedBlobs.length) * 70));
        
        await uploadBytes(storageRef, blob, {
          contentType: 'image/jpeg',
          customMetadata: {
            originalSize: originalFile.size.toString(),
            optimizedSize: blob.size.toString()
          }
        });
        
        const downloadURL = await getDownloadURL(storageRef);
        uploadedUrls.push(downloadURL);
        
        setUploadProgress(Math.round(30 + ((i + 1) / optimizedBlobs.length) * 70));
        console.log(`✅ Image ${i + 1} uploaded`);
      } catch (error) {
        console.error(`❌ Error uploading image ${i + 1}:`, error);
        alert(`Failed to upload image ${i + 1}. Please try again.`);
        throw error;
      }
    }
    
    setUploadProgress(100);
    setTimeout(() => setUploadProgress(0), 1000);
    
    return uploadedUrls;
  } catch (error) {
    console.error('❌ Error in image optimization/upload:', error);
    throw error;
  }
};

const handleFileSelect = (e) => {
  const files = Array.from(e.target.files);
  
  // Validate each file
  const validationResults = files.map(file => 
    ImageOptimizationService.validateImage(file)
  );
  
  const invalidFiles = validationResults.filter(r => !r.valid);
  
  if (invalidFiles.length > 0) {
    alert(invalidFiles[0].error);
    e.target.value = ''; // Clear input
    return;
  }
  
  setFormData({ ...formData, newImages: files });
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      console.log('💾 Saving menu item...');

      let updatedImages = [...formData.images];

      if (formData.newImages?.length > 0) {
        console.log('📤 Uploading new images...');
        const uploadedUrls = await uploadImagesToStorage(formData.newImages);
        updatedImages = [...updatedImages, ...uploadedUrls];
        console.log('✅ All images uploaded');
      }

      const itemData = {
        name: formData.name,
        priceHalf: formData.priceHalf ? Number(formData.priceHalf) : null,
        priceFull: Number(formData.priceFull),
        category: formData.category,
        subcategory: formData.subcategory,
        description: formData.description,
        rating: Number(formData.rating),
        ratingsCount: Number(formData.ratingsCount),
        portionOptions: formData.portionOptions,
        images: updatedImages,
        inStock: formData.inStock,
        recipe: formData.recipe // ✅ ADD THIS LINE
      };

      if (editingItem) {
        const itemId = editingItem.id;
        const menuItemRef = doc(db, 'restaurants', restaurantId, 'menu', itemId);
        
        await setDoc(menuItemRef, itemData, { merge: true });
        
        setMenu(prevMenu => 
          prevMenu.map(item => 
            item.id === itemId ? { ...item, ...itemData } : item
          )
        );
        
        console.log('✅ Updated existing item:', itemId);
      } else {
        const newItemId = `item_${Date.now()}`;
        const newItem = { id: newItemId, ...itemData };
        const menuItemRef = doc(db, 'restaurants', restaurantId, 'menu', newItemId);
        
        await setDoc(menuItemRef, newItem);
        
        setMenu(prevMenu => [...prevMenu, newItem]);
        
        console.log('✅ Added new item:', newItemId);
      }

      StorageService.clearCache();

      closeModal();
if (optimizationInfo) {
  alert(`✅ Menu item saved!\n💰 Storage saved: ${optimizationInfo.savedSize} (${optimizationInfo.savingsPercent})`);
} else {
  alert('✅ Menu item saved successfully!');
}    } catch (error) {
      console.error('❌ Error saving menu item:', error);
      alert('Failed to save menu item. Please try again.');
      await loadMenu();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const images = item.images || [];
    setFormData({
      name: item.name || '',
      priceHalf: item.priceHalf != null ? item.priceHalf.toString() : '',
      priceFull: item.priceFull != null ? item.priceFull.toString() : '',
      category: item.category || '',
      subcategory: item.subcategory || '',
      description: item.description || '',
      rating: item.rating || 4,
      ratingsCount: item.ratingsCount || 1,
      portionOptions: item.portionOptions || ['Half', 'Full'],
      images: images,
      newImages: [],
      inStock: item.inStock !== undefined ? item.inStock : true,
      recipe: item.recipe || { ingredients: [] } // ✅ ADD THIS LINE

    });
    setImageKeys(images.map((_, idx) => `img_${Date.now()}_${idx}`));
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        setSaving(true);
        console.log('🗑️ Deleting menu item:', id);
        
        setMenu(prevMenu => prevMenu.filter(item => item.id !== id));
        
        if (featuredItems.includes(id)) {
          const updatedFeatured = featuredItems.filter(fId => fId !== id);
          setFeaturedItems(updatedFeatured);
          const restaurantRef = doc(db, 'restaurants', restaurantId);
          await setDoc(restaurantRef, { featuredItems: updatedFeatured }, { merge: true });
        }
        
        const menuItemRef = doc(db, 'restaurants', restaurantId, 'menu', id);
        await deleteDoc(menuItemRef);
        
        StorageService.clearCache();
        
        console.log('✅ Menu item deleted');
        alert('✅ Menu item deleted successfully!');
      } catch (error) {
        console.error('❌ Error deleting menu item:', error);
        alert('Failed to delete menu item. Please try again.');
        await loadMenu();
      } finally {
        setSaving(false);
      }
    }
  };

  // ✅ FIXED: Toggle Stock Status
  const toggleStockStatus = async (itemId, currentStatus) => {
    try {
      setSaving(true);
      console.log('🔄 Toggling stock status for:', itemId, 'Current:', currentStatus);
      
      const newStatus = currentStatus === true ? false : true;
      console.log('📦 New stock status will be:', newStatus);
      
      const menuItemRef = doc(db, 'restaurants', restaurantId, 'menu', itemId);
      
      await setDoc(menuItemRef, { inStock: newStatus }, { merge: true });
      
      setMenu(prevMenu =>
        prevMenu.map(item =>
          item.id === itemId ? { ...item, inStock: newStatus } : item
        )
      );
      
      StorageService.clearCache();
      
      console.log(`✅ Stock status updated successfully: ${newStatus ? 'IN STOCK' : 'OUT OF STOCK'}`);
      alert(`✅ Item marked as ${newStatus ? 'IN STOCK' : 'OUT OF STOCK'}`);
    } catch (error) {
      console.error('❌ Error toggling stock status:', error);
      alert('Failed to update stock status. Please try again.');
      await loadMenu();
    } finally {
      setSaving(false);
    }
  };

  const toggleFeaturedItem = async (itemId) => {
    try {
      setSaving(true);
      
      let updatedFeatured;
      if (featuredItems.includes(itemId)) {
        updatedFeatured = featuredItems.filter(id => id !== itemId);
      } else {
        if (featuredItems.length >= 5) {
          alert('You can only have 5 featured items at a time!');
          setSaving(false);
          return;
        }
        updatedFeatured = [...featuredItems, itemId];
      }
      
      setFeaturedItems(updatedFeatured);
      
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      await setDoc(restaurantRef, { featuredItems: updatedFeatured }, { merge: true });
      
      StorageService.clearCache();
      
      console.log('✅ Featured items updated');
    } catch (error) {
      console.error('❌ Error toggling featured item:', error);
      alert('Failed to update featured items. Please try again.');
      await loadMenu();
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = useCallback((index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return { ...prev, images: newImages };
    });
    setImageKeys(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    setFormData(prev => {
      const oldIndex = imageKeys.findIndex(key => key === active.id);
      const newIndex = imageKeys.findIndex(key => key === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return prev;
      
      const newImages = arrayMove(prev.images, oldIndex, newIndex);
      return { ...prev, images: newImages };
    });
    
    setImageKeys(prev => {
      const oldIndex = prev.findIndex(key => key === active.id);
      const newIndex = prev.findIndex(key => key === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, [imageKeys]);

  const handlePortionToggle = (portion) => {
    setFormData(prev => {
      const options = prev.portionOptions.includes(portion)
        ? prev.portionOptions.filter(p => p !== portion)
        : [...prev.portionOptions, portion];
      
      return options.length > 0 ? { ...prev, portionOptions: options } : prev;
    });
  };

  const filteredMenu = useMemo(() => {
    return menu.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [menu, searchTerm, filterCategory]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleFilterChange = useCallback((e) => {
    setFilterCategory(e.target.value);
  }, []);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Menu Management</h1>
        <div className="flex gap-3">
          <button
            onClick={onNavigateToCategories}
            disabled={saving}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50 transition"
          >
            <Tag className="w-5 h-5" /> Manage Categories
          </button>
          <button
            onClick={() => setShowModal(true)}
            disabled={saving}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50 transition"
          >
            <Plus className="w-5 h-5" /> Add Item
          </button>
        </div>
      </div>

      {featuredItems.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-l-4 border-orange-400 p-4 mb-6 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-5 h-5 text-orange-500" />
            <h3 className="font-bold text-gray-800">Featured Items ({featuredItems.length}/5)</h3>
          </div>
          <p className="text-sm text-gray-600">
            These items will be shown in the special carousel on customer menu page
          </p>
        </div>
      )}

      {categories.length === 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex items-center">
            <Tag className="w-5 h-5 text-yellow-400 mr-3" />
            <div>
              <p className="text-sm text-yellow-700">
                No categories found! Please add categories first to organize your menu items.
              </p>
              <button
                onClick={onNavigateToCategories}
                className="text-sm text-yellow-800 underline font-semibold mt-1"
              >
                Go to Categories Page →
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-64 relative">
            <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search menu items..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <select
            value={filterCategory}
            onChange={handleFilterChange}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option>All</option>
            {categories?.map(cat => <option key={cat}>{cat}</option>)}
          </select>
        </div>
      </div>

      {filteredMenu.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <ChefHat className="w-20 h-20 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {menu.length === 0 ? 'No Menu Items Yet' : 'No items match your search'}
          </h3>
          <p className="text-gray-500 mb-4">
            {menu.length === 0 ? 'Start adding items to your menu' : 'Try a different search or filter'}
          </p>
          {menu.length === 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600"
            >
              Add Your First Item
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMenu.map(item => {
            const isInStock = item.inStock === true;
            
            return (
              <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition relative">
                {/* ✅ FIXED: OUT OF STOCK BADGE - Not blocking clicks */}
                {!isInStock && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10 shadow-lg pointer-events-none">
                    OUT OF STOCK
                  </div>
                )}
                
                {featuredItems.includes(item.id) && (
                  <div className="absolute top-2 left-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10 flex items-center gap-1 shadow-lg">
                    <Flame className="w-3 h-3" />
                    FEATURED
                  </div>
                )}
                
                <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden relative">
                  {item.images?.[0] ? (
                    <img 
                      src={item.images[0]} 
                      alt={item.name} 
                      className={`object-cover w-full h-full ${!isInStock ? 'opacity-50 grayscale' : ''}`}
                    />
                  ) : (
                    <ChefHat className="w-20 h-20 text-gray-300 opacity-50" />
                  )}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-bold text-gray-800">{item.name}</h3>
                    <div className="text-right">
                      {item.priceHalf && (
                        <div className="text-sm text-gray-600">Half: ₹{item.priceHalf}</div>
                      )}
                      <div className="text-orange-500 font-bold">Full: ₹{item.priceFull}</div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{item.description}</p>
                  <div className="flex gap-2 mb-3 flex-wrap">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{item.category}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">{item.subcategory}</span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                      ⭐ {item.rating} ({item.ratingsCount})
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        disabled={saving}
                        className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-1 disabled:opacity-50 transition"
                      >
                        <Edit className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={saving}
                        className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-600 flex items-center justify-center gap-1 disabled:opacity-50 transition"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                    
                    {/* ✅ FIXED: Stock toggle button - Always clickable, no overlay blocking */}
                    <button
                      onClick={() => toggleStockStatus(item.id, isInStock)}
                      disabled={saving}
                      className={`w-full py-2 rounded-lg flex items-center justify-center gap-2 font-semibold disabled:opacity-50 transition ${
                        isInStock
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 border-2 border-green-300'
                          : 'bg-red-100 text-red-700 hover:bg-red-200 border-2 border-red-300'
                      }`}
                    >
                      {isInStock ? (
                        <>
                          <PackageCheck className="w-4 h-4" /> In Stock - Click to Mark Out
                        </>
                      ) : (
                        <>
                          <PackageX className="w-4 h-4" /> Out of Stock - Click to Mark In
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => toggleFeaturedItem(item.id)}
                      disabled={saving}
                      className={`w-full py-2 rounded-lg flex items-center justify-center gap-2 font-semibold disabled:opacity-50 transition ${
                        featuredItems.includes(item.id)
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {featuredItems.includes(item.id) ? (
                        <>
                          <Check className="w-4 h-4" /> Featured Item
                        </>
                      ) : (
                        <>
                          <Flame className="w-4 h-4" /> Mark as Featured
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700" disabled={saving}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                  disabled={saving}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stock Status</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.inStock === true}
                      onChange={() => setFormData({ ...formData, inStock: true })}
                      className="w-4 h-4 text-green-500 focus:ring-green-500"
                      disabled={saving}
                    />
                    <span className="text-gray-700 flex items-center gap-1">
                      <PackageCheck className="w-4 h-4 text-green-600" />
                      In Stock
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.inStock === false}
                      onChange={() => setFormData({ ...formData, inStock: false })}
                      className="w-4 h-4 text-red-500 focus:ring-red-500"
                      disabled={saving}
                    />
                    <span className="text-gray-700 flex items-center gap-1">
                      <PackageX className="w-4 h-4 text-red-600" />
                      Out of Stock
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Available Portions</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.portionOptions.includes('Half')}
                      onChange={() => handlePortionToggle('Half')}
                      className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                      disabled={saving}
                    />
                    <span className="text-gray-700">Half</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.portionOptions.includes('Full')}
                      onChange={() => handlePortionToggle('Full')}
                      className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                      disabled={saving}
                    />
                    <span className="text-gray-700">Full</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {formData.portionOptions.includes('Half') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Half Price (₹)</label>
                    <input
                      type="number"
                      value={formData.priceHalf}
                      onChange={(e) => setFormData({ ...formData, priceHalf: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required={formData.portionOptions.includes('Half')}
                      disabled={saving}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Price (₹)</label>
                  <input
                    type="number"
                    value={formData.priceFull}
                    onChange={(e) => setFormData({ ...formData, priceFull: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                    disabled={saving}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                  {categories.length === 0 && (
                    <button
                      type="button"
                      onClick={onNavigateToCategories}
                      className="text-xs text-blue-600 underline mt-1"
                    >
                      Add categories first →
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    required
                    disabled={saving}
                  >
                    <option value="">Select Subcategory</option>
                    {subcategories.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    disabled={saving}
                  >
                    {[1,2,3,4,5].map(r => <option key={r} value={r}>{r} ⭐</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ratings Count</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.ratingsCount}
                    onChange={(e) => setFormData({ ...formData, ratingsCount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    disabled={saving}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="3"
                  required
                  disabled={saving}
                />
              </div>



{/* ✅ ADD THIS ENTIRE SECTION HERE: */}
<div className="border-t pt-4">
  <div className="flex justify-between items-center mb-3">
    <div>
      <h4 className="font-bold text-gray-700">Recipe Ingredients</h4>
      <p className="text-xs text-gray-500">Link inventory items used in this dish (for auto stock deduction)</p>
    </div>
    <button
      type="button"
      onClick={() => {
        const newIngredients = [...(formData.recipe?.ingredients || [])];
        newIngredients.push({ inventoryItemId: '', quantity: 0, unit: 'kg' });
        setFormData({ 
          ...formData, 
          recipe: { 
            ingredients: newIngredients 
          } 
        });
      }}
      className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-sm"
    >
      <Plus className="w-4 h-4" />
      Add Ingredient
    </button>
  </div>

  <div className="space-y-2 max-h-60 overflow-y-auto">
    {formData.recipe?.ingredients?.map((ingredient, index) => (
      <div key={index} className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-6">
          <select
            value={ingredient.inventoryItemId}
            onChange={(e) => {
              const newIngredients = [...formData.recipe.ingredients];
              newIngredients[index].inventoryItemId = e.target.value;
              const selectedItem = inventoryItems.find(i => i.id === e.target.value);
              if (selectedItem) {
                newIngredients[index].unit = selectedItem.unit;
              }
              setFormData({ 
                ...formData, 
                recipe: { 
                  ingredients: newIngredients 
                } 
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Select Ingredient</option>
            {inventoryItems.map(item => (
              <option key={item.id} value={item.id}>
                {item.name} ({item.unit})
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-3">
          <input
            type="number"
            step="0.01"
            placeholder="Quantity"
            value={ingredient.quantity}
            onChange={(e) => {
              const newIngredients = [...formData.recipe.ingredients];
              newIngredients[index].quantity = parseFloat(e.target.value) || 0;
              setFormData({ 
                ...formData, 
                recipe: { 
                  ingredients: newIngredients 
                } 
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        <div className="col-span-2">
          <input
            type="text"
            value={ingredient.unit}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
          />
        </div>

        <div className="col-span-1">
          <button
            type="button"
            onClick={() => {
              const newIngredients = formData.recipe.ingredients.filter((_, i) => i !== index);
              setFormData({ 
                ...formData, 
                recipe: { 
                  ingredients: newIngredients 
                } 
              });
            }}
            className="w-full p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    ))}
  </div>
</div>


              <div>
<label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
  <ImageIcon className="w-4 h-4" />
  Images (Auto-optimized)
</label>
                <input
  type="file"
  accept="image/*"
  multiple
  onChange={handleFileSelect}
  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
  disabled={saving}
/>
<p className="text-xs text-gray-500 mt-1">
  📊 Upload your menu image 
</p>
                
                {uploadProgress > 0 && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
<p className="text-xs text-gray-600 mt-1">
  {uploadProgress < 30 ? 'Optimizing images...' : 'Uploading...'}
</p>
                  </div>
                )}
                  {optimizationInfo && (
                  <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-green-800 mb-1">💰 Image getting Saved</p>
                    <div className="text-xs text-green-700 space-y-1">
                      {/* <div>Original: {optimizationInfo.originalSize} → Optimized: {optimizationInfo.optimizedSize}</div> */}
                      {/* <div className="font-bold">Saved: {optimizationInfo.savedSize} ({optimizationInfo.savingsPercent})</div> */}
                    </div>
                  </div>
                )}
                {formData.images.length > 0 && (
                  <DndContext 
                    collisionDetection={closestCenter} 
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                  >
                    <SortableContext items={imageKeys} strategy={verticalListSortingStrategy}>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {formData.images.map((img, index) => (
                          <SortableImage
                            key={imageKeys[index]}
                            id={index}
                            uniqueKey={imageKeys[index]}
                            src={img}
                            onRemove={handleRemoveImage}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
                
                {formData.newImages.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
  <Check className="w-3 h-3 text-green-600" />
  {formData.newImages.length} new image(s) ready to optimize & upload
</p>
                )}

               
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition"
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
{uploadProgress > 0 ? `${uploadProgress < 30 ? 'Optimizing' : 'Uploading'} ${uploadProgress}%` : 'Saving...'}                  </>
                ) : (
                  <>{editingItem ? 'Update' : 'Add'} Item</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerMenu;